// OGS asset status — module singleton (NOT React state) recording an automatic
// capability downgrade so a viewport pill can warn the user. Written by the
// runner boot path (React-free), read by OGSAssetWarningIndicator via subscribe.
// Mirrors the textureQuality.js singleton pattern. Loud, per CLAUDE.md UX rules:
// a silent fp16→fp32 or WebGPU→CPU-lift fallback is exactly what this surfaces.

const state = {
  downgrade: null, // { kind: 'geom'|'appr'|'lift', reason } | null
  precision: null, // { requested: 'fp16'|'fp32', geom: 'fp16'|'fp32', appr: 'fp16'|'fp32' } | null
  rigProblem: null, // { slug, cause, detail } | null
}

// What each downgrade actually costs, in the pill's own words — a decoder losing fp16 and the
// lift losing WebGPU are different losses, and one shared sentence would misreport whichever
// it wasn't written for.
const DOWNGRADE_MESSAGES = {
  geom: 'Quantized (fp16) decoder unavailable — using full-size fp32 (larger download, slower).',
  appr: 'Quantized (fp16) decoder unavailable — using full-size fp32 (larger download, slower).',
  lift: 'WebGPU lift unavailable — lifting Gaussians on the CPU (seconds per frame, not milliseconds).',
}

/** The pill's sentence for a downgrade kind. An unknown kind still names itself, never blank. */
export function downgradeMessage(kind) {
  return DOWNGRADE_MESSAGES[kind] ?? `${kind} unavailable — running a slower fallback.`
}
const subscribers = new Set()

function notify() {
  for (const fn of subscribers) {
    try { fn() } catch (e) { console.error('[ogsAssetStatus]', e) }
  }
}

// Record an automatic capability fallback: a decoder off fp16, or the lift off WebGPU.
export function reportDowngrade(kind, reason) {
  state.downgrade = { kind, reason }
  console.warn(`[ogsAssetStatus] ${downgradeMessage(kind)} (${kind}: ${reason})`)
  notify()
}

export function dismissDowngrade() {
  if (!state.downgrade) return
  state.downgrade = null
  notify()
}

// Record that this character's head cannot be posed, so it renders at the
// bundle's NEUTRAL pose.
//
// This is the loud half of "the exact rig is the default": the avatar still
// draws — a neutral face is a face — but a head that never moves is otherwise
// indistinguishable from a hung pipeline, and someone would go debugging the
// decoder instead. Dismissible, like the downgrade pill: it is a state of this
// character, not a transient failure to retry.
//
// `cause` picks the pill's HEADLINE and `detail` explains underneath, because
// the ways to get here have different fixes and a single message serves none of
// them well:
//   'upload-dna' — no rig at all; drop this character's head.dna on the browser.
//   'drop-flag'  — a ?rig= asked for a rig this character doesn't have.
//   're-export'  — the bundle declares no rig this app can load.
//   'mismatch'   — a rig IS loaded and does not fit this bundle's mesh
//                  (OGSBranch.calibrate refused the fit): the wrong identity's
//                  DNA, which is the one case where an asset exists and is wrong
//                  rather than absent.
export function reportRigProblem(slug, { cause = 'upload-dna', detail = '' } = {}) {
  state.rigProblem = { slug, cause, detail }
  console.warn(`[ogsAssetStatus] '${slug}' rig problem (${cause}) — head stays at the neutral pose: ${detail}`)
  notify()
}

export function dismissRigProblem() {
  if (!state.rigProblem) return
  state.rigProblem = null
  notify()
}

// A character switch must clear the previous character's verdict, or the pill
// outlives the avatar it describes.
export function clearRigProblem() {
  if (state.rigProblem === null) return
  state.rigProblem = null
  notify()
}

// Record the precision requested by the OGS Avatar node vs. what actually built
// per decoder (fp16 can auto-downgrade). Drives the node's "active" readout.
export function reportPrecision(precision) {
  state.precision = precision
  notify()
}

export function getStatus() {
  return { downgrade: state.downgrade, precision: state.precision, rigProblem: state.rigProblem }
}

export function subscribe(fn) {
  subscribers.add(fn)
  return () => subscribers.delete(fn)
}

if (typeof window !== 'undefined') {
  window.__OGS_ASSET_STATUS__ = { getStatus, reportDowngrade, reportPrecision, dismissDowngrade, subscribe }
}
